import { Injectable, inject } from '@angular/core';
import { Apollo, gql, MutationResult } from 'apollo-angular';
import { Observable } from 'rxjs';
import { CREATE_USER, GET_USER, UPDATE_USER } from '../graphql/user.operations';
import {
  ICreateUser,
  IUserByEmailResponse,
  IUpdateUser,
  IUser,
} from '@interface/user.interface';
import { ApolloQueryResult } from '@apollo/client';

@Injectable({
  providedIn: 'root',
})
export class UsersApiService {
  private readonly apollo = inject(Apollo);

  public getUserByEmail(
    email: string,
  ): Observable<ApolloQueryResult<IUserByEmailResponse>> {
    return this.apollo.query<IUserByEmailResponse>({
      query: GET_USER,
      variables: { email },
    });
  }  

  public createUser(user: ICreateUser): Observable<MutationResult<IUser>> {
    return this.apollo.mutate<IUser>({
      mutation: CREATE_USER,
      variables: { user },
    });
  }

  public updateUser(email: string, userData: IUpdateUser): Observable<MutationResult<IUser>> {
    return this.apollo.mutate<IUser>({
      mutation: UPDATE_USER,
      variables: { email, userData },
    });
  }

  public getOrganisations(): Observable<ApolloQueryResult<{ organizations: any[] }>> {
    return this.apollo.query<{ organizations: any[] }>({
      query: gql`
        query {
          organizations {
            id
            name
            allowedDomains
            companyUrl
            subscription {
              subscriptionId
              status
            }
          }
        }
      `
    });
  }

  public getAllPlans(): Observable<ApolloQueryResult<{ plans: any[] }>> {
    return this.apollo.query<{ plans: any[] }>({
      query: gql`
        query { plans { id name description stripePlanId rate { amount currency interval } featureQuotaMapping { featureId quota featureKey pricingLabel } } }
      `
    });
  }

  public createOrganisation(orgData: {
    name: string;
    allowedDomains: string[];
    companyUrl: string;
    createdBy: string;
  }): Observable<MutationResult<{ createOrganization: any }>> {
    return this.apollo.mutate<{ createOrganization: any }>({
      mutation: gql`
        mutation($input: CreateOrganizationInput!) {
          createOrganization(input: $input) {
            id
            name
            allowedDomains
            companyUrl
            subscription {
              subscriptionId
              status
              currentPeriodStart
              currentPeriodEnd
              customerId
              priceId
              amount
              currency
              interval
            }
          }
        }
      `,
      variables: {
        input: orgData
      }
    });
  }

  public getAccounts(organizationId: string): Observable<ApolloQueryResult<{ accountsByOrganization: any[] }>> {
    return this.apollo.query<{ accountsByOrganization: any[] }>({
      query: gql`
        query($organizationId: String!) {
          accountsByOrganization(organizationId: $organizationId) {
            id
            name
            description
            organization {
              id
              name
            }
          }
        }
      `,
      variables: {
        organizationId
      }
    });
  }

  public createGroup(input: {
    name: string;
    description?: string;
    accountId: string;
  }): Observable<MutationResult<{ createGroup: any }>> {
    return this.apollo.mutate<{ createGroup: any }>({
      mutation: gql`
        mutation($input: CreateGroupInput!) {
          createGroup(input: $input) {
            id
            name
            description
            account {
              id
              name
            }
          }
        }
      `,
      variables: {
        input
      }
    });
  }

  public createDefaultAccount(input: {
    name: string;
    description: string;
    organizationId: string;
    createdBy: string;
  }): Observable<MutationResult<{ createAccount: any }>> {
    return this.apollo.mutate<{ createAccount: any }>({
      mutation: gql`
        mutation($input: CreateAccountInput!) {
          createAccount(input: $input) {
            id
            name
            description
            organization {
              id
              name
            }
          }
        }
      `,
      variables: {
        input
      }
    });
  }

  public getGroups(accountId: string): Observable<ApolloQueryResult<{ groupsByAccount: any[] }>> {
    return this.apollo.query<{ groupsByAccount: any[] }>({
      query: gql`
        query($accountId: ID!) {
          groupsByAccount(accountId: $accountId) {
            id
            name
            description
            account {
              id
              name
            }
            members {
              id
              firstName
              lastName
              email
            }
          }
        }
      `,
      variables: {
        accountId
      }
    });
  }

  public addGroupMember(groupId: string, userId: string): Observable<MutationResult<{ addGroupMember: any }>> {
    return this.apollo.mutate<{ addGroupMember: any }>({
      mutation: gql`
        mutation($groupId: ID!, $userId: ID!) {
          addGroupMember(groupId: $groupId, userId: $userId) {
            id
            name
            members {
              id
              firstName
              lastName
            }
          }
        }
      `,
      variables: {
        groupId,
        userId
      }
    });
  }

  public getRoles(): Observable<ApolloQueryResult<{ roles: any[] }>> {
    return this.apollo.query<{ roles: any[] }>({
      query: gql`
        query { roles { id name description privileges { name description category } } }
      `
    });
  }

  public getPlanById(id: string): Observable<ApolloQueryResult<{ plan: any }>> {
    return this.apollo.query<{ plan: any }>({
      query: gql`
        query($id: String!) {
          plan(id: $id) {
            id
            name
            description
            stripePlanId
            rate {
              amount
              currency
              interval
            }
            featureQuotaMapping {
              featureId
              quota
            }
          }
        }
      `,
      variables: {
        id
      }
    });
  }

  public getUsersByOrganization(organizationId: string): Observable<ApolloQueryResult<{ usersByOrganization: any[] }>> {
    return this.apollo.query<{ usersByOrganization: any[] }>({
      query: gql`
        query($organizationId: String!) {
          usersByOrganization(organizationId: $organizationId) {
            id
            firstName
            lastName
            email
            roles {
              id
              name
            }
            account {
              id
              name
            }
          }
        }
      `,
      variables: {
        organizationId
      }
    });
  }
}

